import * as React from "react";
import { FC, useCallback, useEffect, useState } from "react";
import { CloseButton } from "../../Elements/CloseButton";
import { PopupBody, PopupFooter, PopupHeader, Popup } from "../Popup";
import { useNomaContext } from "../../Contexts/MapContext";
import { PopupButton } from "../../Elements/PopupButton";
import { FormSection } from "../../Forms/FormSection";
import { useRegistrationContext } from "../../Contexts/RegistrationContext";
import { FormInput } from "../../Forms/formFields";
import { Feature } from "ol";
import {
    IAttachmentFile,
    IAttachmentPath,
    IObjectIDs,
    printPdf,
    requestObjectIds,
    sendEmail,
    updateFeatures,
    uploadFile
} from "../../Map/rest";
import { ObjectSection } from "./ObjectSection";
import { PrivacySection } from "./PrivacySection";
import { UserConsentCheck } from "./UserConsent";
import { SummarySection } from "./SummarySection";
import { ISubmitStatus, SubmitSection } from "./SubmitSection";
import { ApplicantType, AttachmentType, PrintType, Result, UploadState } from "../../Map/enums";
import { AxiosResponse } from "axios";
import { objectIdGap } from "../../Map/config";
import "./Registration.scss";

enum PageId {
    PrivacyNotice = 0,
    ObjectInfo = 1,
    ApplicationForm = 2,
    Summary = 3,
    Submit = 4
}

export const Registration: FC = () => {
    const [page, setPage] = useState<PageId>(PageId.PrivacyNotice);
    const [idRequested, setIdRequested] = useState<boolean>(false);
    const [submitStatus, setSubmitStatus] = useState<ISubmitStatus>();
    const [registrationCompleted, setRegistrationCompleted] = useState<Result | undefined>(undefined);
    const [error, setError] = useState<boolean>(false);

    const {
        userConsent,
        setUserConsent,
        setValidateForm,
        isFormValid,
        formData,
        rawData,
        formFeature,
        setFormFeature,
        applicantType,
        setResetForm,
        setFormDefaultData,
        attachments,
        summaryData
    } = useRegistrationContext();

    const { isMobile, registerFeature, setRegisterFeature } = useNomaContext();
    const x = document.body.clientWidth / 2 - 300;

    const OnApplyForm = () => {
        setValidateForm(Date.now());
    };

    const onObjectInfoBack = () => {
        setUserConsent(false);
        setPage(PageId.PrivacyNotice);
    };
    const onFormBack = () => {
        setUserConsent(false);
        setError(false);
        setIdRequested(false);
        setResetForm(Date.now());
        setPage(PageId.ObjectInfo);
    };
    const onSummaryBack = () => {
        setValidateForm(0);
        setPage(PageId.ApplicationForm);
        formData && setFormDefaultData(formData);
    };

    const createNewFeature = () => {
        const newFeature = new Feature(registerFeature.getGeometry());

        const properties = {
            FORMINPUTSTATE: 1,
            APPLICATIONDATE: Date.now(),
            APPLICANTTYPE: applicantType,
            LVMFORESTRYCODE: registerFeature.get("LVMFORESTRYCODE"),
            REGIONATVK: registerFeature.get("REGIONATVK"),
            MUNICIPALITYATVK: registerFeature.get("MUNICIPALITYATVK"),
            PROPERTYCADASTRALNUMBER: registerFeature.get("PROPERTYCADASTRALNUMBER"),
            PROPERTYTITLE: registerFeature.get("PROPERTYTITLE"),
            CADASTER: registerFeature.get("CADASTER"),
            TYPEOFLEASEGRANTING: registerFeature.get("TYPEOFLEASEGRANTING")
        };
        registerFeature.get("LAYERID") === "1"
            ? (properties["WINDPLOTID"] = Number(registerFeature.get("OBJECTID")) - objectIdGap)
            : (properties["LANDPLOTID"] = registerFeature.get("OBJECTID"));
        newFeature.setProperties(properties);

        return newFeature;
    };

    const appendFormData = useCallback(() => {
        formFeature.setProperties({
            FORMINPUTSTATE: 100,
            APPLICANTNAME:
                applicantType === ApplicantType.Individual
                    ? formData[FormInput.Name].toString() + " " + formData[FormInput.Surname]
                    : formData[FormInput.Company],
            APPLICANTCODE:
                applicantType === ApplicantType.Individual
                    ? formData[FormInput.PersonalCode]
                    : formData[FormInput.RegistrationNumber],
            APPLICANTADDRESS: formData[FormInput.Address],
            APPLICANTPHONE: formData[FormInput.Phone],
            APPLICANTEMAIL: formData[FormInput.Email],
            DESCRIPTION: formData[FormInput.Notes],
            CONTACTPERSON: formData[FormInput.ContactPerson]
        });
        return formFeature;
    }, [formData, applicantType]);

    // STEP 1 - request ObjectId on first input change
    useEffect(() => {
        if (!Object.keys(rawData).length || formFeature || idRequested) {
            return;
        }
        setIdRequested(true);
        const newFeature = createNewFeature();
        requestObjectIds([newFeature]).then((ids: IObjectIDs[]) => {
            if (!ids || !ids[0]) {
                console.log("Connection error while requesting OBJECTID");
                setError(true);
                return;
            }
            newFeature.setId(ids[0].objectId);
            newFeature.setProperties({
                OBJECTID: ids[0].objectId,
                GLOBALID: ids[0].globalId
            });
            setFormFeature(newFeature);
            setIdRequested(false);
            console.log("piešķirts OBJECTID", ids[0].objectId);
        });
    }, [rawData, applicantType, idRequested]);

    // STEP 2 - append form data to feature - GATAVS IESNIEGŠANAI
    useEffect(() => {
        if (isFormValid && !formFeature) {
            console.log("Can not update feature without OBJECTID");
            setError(true);
            return;
        }
        isFormValid &&
            updateFeatures([appendFormData()]).then((results: AxiosResponse<any>) => {
                if (!results || results.status !== 200 || !results.data.updateResults[0].success) {
                    console.log("error with updateFeatures");
                    return;
                }
                setPage(PageId.Summary);
            });
    }, [isFormValid]);

    // STEP 3 - iesniegts - IESNIEGTS
    const onSubmit = async () => {
        setPage(PageId.Submit);
        /*** Upload attachments */
        attachments && attachments.length ? await uploadAttachments() : await Promise.resolve();

        /*** Create PDF */
        const pdfResult = await printPdf(summaryData, PrintType.File);
        if (pdfResult && pdfResult.status === 0) {
            setSubmitStatus({ name: UploadState.CreatePdf, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            return;
        }
        setSubmitStatus({ name: UploadState.CreatePdf, result: Result.Success });
        const file = new File([pdfResult.data], "pieteikums.pdf", { type: "application/pdf" });
        console.log("pdf print result", pdfResult);

        /*** Upload PDF */
        const uploadedPdfFileName = await uploadPdf(file);
        if (!uploadedPdfFileName) {
            setSubmitStatus({ name: UploadState.UploadPdf, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            return;
        }
        setSubmitStatus({ name: UploadState.UploadPdf, result: Result.Success });

        /*** Submit Form */
        formFeature.set("FORMINPUTSTATE", 110, true);
        const featureUpdate = await updateFeatures([formFeature]);
        if (!featureUpdate || featureUpdate.status !== 200 || !featureUpdate.data.updateResults[0].success) {
            setSubmitStatus({ name: UploadState.SubmitForm, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            return;
        }
        setSubmitStatus({ name: UploadState.SubmitForm, result: Result.Success });
        console.log("Form feature submitted", featureUpdate);

        /*** Send Email with attached PDF */
        const emailAttachment: IAttachmentFile = {
            type: AttachmentType.File,
            endpoint: "FileProvider", // endpoint name, taken from Mailer appsettings.config
            resourceId: uploadedPdfFileName
        };

        /*** Send Email with PDF as path */
        // const emailAttachment: IAttachmentPath = {
        //     type: AttachmentType.Path,
        //     // url: pdfResult.data.path
        //     url: uploadedPdfFileName
        // };

        const emailResult = await sendEmail(formFeature.get("APPLICANTEMAIL"), [emailAttachment]);
        console.log("send email result", emailResult);
        if (emailResult && emailResult.status === 0) {
            setSubmitStatus({ name: UploadState.SendEmail, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            return;
        }
        setSubmitStatus({ name: UploadState.SendEmail, result: Result.Success });
        setRegistrationCompleted(Result.Success);
    };

    const uploadPdf = async (file: File): Promise<string> => {
        const fileFeature: Feature = new Feature();
        fileFeature.set("FILEDESCRIPTION", file.name);

        const fileFeatureOIDs = await requestObjectIds([fileFeature], "1");
        const oid = fileFeatureOIDs[0];

        const uploadResult = await uploadFile(file, oid.objectId.toString());
        if (!uploadResult || uploadResult.status !== 200 || !uploadResult.data.fileName) {
            return;
        }

        fileFeature.setProperties({
            OBJECTID: oid.objectId,
            FILEID: uploadResult.data.fileName,
            FORMGLOBALID: formFeature.get("GLOBALID")
        });
        const featureUpdate = await updateFeatures([fileFeature], "1");

        return !featureUpdate || featureUpdate.status !== 200 || !featureUpdate.data.updateResults[0].success
            ? null
            : uploadResult.data.fileName;
    };

    const uploadAttachments = async () => {
        const fileFeatures: Feature[] = Array.from(attachments, (file) => {
            const fileFeature = new Feature();
            fileFeature.set("FILEDESCRIPTION", file.name);
            return fileFeature;
        });

        const fileFeatureOIDs = await requestObjectIds(fileFeatures, "1");
        return Promise.all(
            fileFeatureOIDs.map(async (oid, i) =>
                uploadFile(attachments[i], oid.objectId.toString()).then((uploadResult) => {
                    if (uploadResult && uploadResult.status === 200 && uploadResult.data.fileName) {
                        fileFeatures[i].setProperties({
                            OBJECTID: oid.objectId,
                            FILEID: uploadResult.data.fileName,
                            FORMGLOBALID: formFeature.get("GLOBALID")
                        });
                        setSubmitStatus({ name: attachments[i].name, result: Result.Success });
                    } else {
                        setSubmitStatus({ name: attachments[i].name, result: Result.Error });
                    }
                })
            )
        )
            .then(() => updateFeatures(fileFeatures, "1"))
            .then((updated) => updated);
    };

    const isUsualCase = !registerFeature || registerFeature.get("TYPEOFLEASEGRANTING") !== 5;

    return (
        <Popup id={"register-popup"} isMobile={isMobile} hasBackdrop={true} defaultPosition={{ x, y: 0 }}>
            <PopupHeader isMobile={isMobile}>
                {page === PageId.PrivacyNotice && <div>PERSONAS DATU APSTRĀDE</div>}
                {page === PageId.ObjectInfo && <div>NOMAS OBJEKTA DATI</div>}
                {page === PageId.ApplicationForm && <div>DATI PAR IESNIEDZĒJU</div>}
                {page === PageId.Summary && <div>PIETEIKUMS</div>}
                {page === PageId.Submit && <div>PIETEIKUMA IESNIEGŠANA</div>}
                <CloseButton onClose={() => setRegisterFeature(null)}></CloseButton>
            </PopupHeader>
            <PopupBody isMobile={isMobile}>
                {page === PageId.PrivacyNotice && <PrivacySection></PrivacySection>}
                {page === PageId.ObjectInfo && (
                    <>
                        <ObjectSection></ObjectSection>
                        {isUsualCase ? (
                            <>
                                <br />
                                <br />
                                <UserConsentCheck onChange={setUserConsent}></UserConsentCheck>
                                <br />
                            </>
                        ) : null}
                    </>
                )}
                {page === PageId.ApplicationForm && <FormSection error={error}></FormSection>}
                {page === PageId.Summary && <SummarySection></SummarySection>}
                {page === PageId.Submit && (
                    <SubmitSection submitStatus={submitStatus} completed={registrationCompleted}></SubmitSection>
                )}
            </PopupBody>
            <PopupFooter isMobile={isMobile}>
                {page === PageId.PrivacyNotice && (
                    <PopupButton
                        title={"Turpināt"}
                        classname={"dark"}
                        onClick={() => setPage(PageId.ObjectInfo)}
                    ></PopupButton>
                )}
                {page === PageId.ObjectInfo && (
                    <>
                        <PopupButton title={"Atpakaļ"} classname={"light"} onClick={onObjectInfoBack}></PopupButton>
                        <PopupButton
                            title={"Turpināt"}
                            classname={"dark"}
                            disabled={isUsualCase ? !userConsent : false}
                            onClick={() => setPage(PageId.ApplicationForm)}
                        ></PopupButton>
                    </>
                )}
                {page === PageId.ApplicationForm && (
                    <>
                        <PopupButton title={"Atpakaļ"} classname={"light"} onClick={onFormBack}></PopupButton>
                        <PopupButton title={"Turpināt"} classname={"dark"} onClick={OnApplyForm}></PopupButton>
                    </>
                )}
                {page === PageId.Summary && (
                    <>
                        <PopupButton title={"Atpakaļ"} classname={"light"} onClick={onSummaryBack}></PopupButton>
                        <PopupButton title={"Iesniegt"} classname={"dark"} onClick={onSubmit}></PopupButton>
                    </>
                )}
            </PopupFooter>
        </Popup>
    );
};
